import { now, dateDiff } from '../temporal-utils';
import { ISummary, CoreStatusExtended } from 'types';

export enum TrackerStep {
  HIDDEN = 0,
  CAS_REQUEST_RECEIVED = 1,
  CAS_REQUEST_REVIEWING = 2,
  CAS_PROCESSING = 3,
  CAS_ISSUED = 4,
}
const trackerHiddenStatuses: Array<string> = [
  CoreStatusExtended.VISA_ISSUED,
  CoreStatusExtended.VISA_REFUSED,
  CoreStatusExtended.DEFERRED,
  CoreStatusExtended.CLOSED_LOST,
];

const MIN_DAYS_SINCE_TASKS_COMPLETE = 1;
const MIN_DAYS_SINCE_CAS_PROCESSING_STARTED = 1;
const CAS_REQUEST_RECEIVED_PROGRESS = 25;
const CAS_REQUEST_REVIEWING_START_PROGRESS = 61;
const CAS_REQUEST_REVIEWING_END_PROGRESS = 25;
const CAS_PROCESSING_START_PROGRESS = 96;
const CAS_PROCESSING_END_PROGRESS = 69;
const CAS_ISSUED_PROGRESS = 100;
const HIDDEN_PROGRESS = 0;
const MAX_LOGARITHMIC_PERCENTAGE_VALUE = 15;

const casProcessingStatuses: Array<string> = [CoreStatusExtended.DRAFT_CAS, CoreStatusExtended.READY_FOR_CAS];

export function getTrackerStatus(summary: ISummary): {
  step: TrackerStep;
  progress: number;
} {
  // if tasks were reopened then use the date before reopening, so the cas tracker doesn't jump back
  const tasksWereReopened =
    typeof summary.tasksCompleteAtBeforeReopening === 'number' && summary.tasksCompleteAtBeforeReopening !== 0;
  const currentDate = now();

  // Convert timestamps to Date objects first
  const completeTimestamp = tasksWereReopened ? summary.tasksCompleteAtBeforeReopening : summary.tasksCompleteAt;
  const completeDate = completeTimestamp ? new Date(completeTimestamp) : new Date();
  const completeDays = dateDiff(completeDate, currentDate, 'days');

  const { readyForCASUpdatedAt } = summary;
  const statusTimestamp = readyForCASUpdatedAt ?? summary.casStatusUpdatedAt;
  const statusDate = new Date(statusTimestamp);
  const statusDays = dateDiff(statusDate, currentDate, 'days');
  const isTrackerHiddenStatus = trackerHiddenStatuses.includes(summary.cas_status);
  const isCasProcessing = casProcessingStatuses.includes(summary.cas_status);
  const isCasIssued = summary.cas_status === CoreStatusExtended.CAS_ISSUED;
  const isTasksComplete = summary.isTasksComplete === true;

  if (!isTasksComplete || isTrackerHiddenStatus) {
    return {
      step: TrackerStep.HIDDEN,
      progress: HIDDEN_PROGRESS,
    };
  }

  if (isCasIssued) {
    return {
      step: TrackerStep.CAS_ISSUED,
      progress: CAS_ISSUED_PROGRESS,
    };
  }

  if (isCasProcessing || readyForCASUpdatedAt) {
    const progress =
      statusDays >= MIN_DAYS_SINCE_CAS_PROCESSING_STARTED
        ? getLogarithmicPercentage(CAS_PROCESSING_START_PROGRESS, CAS_PROCESSING_END_PROGRESS, statusDays + 1)
        : CAS_PROCESSING_END_PROGRESS;
    return { step: TrackerStep.CAS_PROCESSING, progress };
  }

  if (completeDays >= MIN_DAYS_SINCE_TASKS_COMPLETE) {
    const progress = getLogarithmicPercentage(
      CAS_REQUEST_REVIEWING_START_PROGRESS,
      CAS_REQUEST_REVIEWING_END_PROGRESS,
      completeDays + 1,
    );
    return { step: TrackerStep.CAS_REQUEST_REVIEWING, progress };
  }

  return { step: TrackerStep.CAS_REQUEST_RECEIVED, progress: CAS_REQUEST_RECEIVED_PROGRESS };
}

export function getLogarithmicPercentage(maxPctValue: number, minPctValue: number, value: number) {
  const maxValue = MAX_LOGARITHMIC_PERCENTAGE_VALUE;

  if (value <= 1) {
    return minPctValue;
  } else if (value > maxValue) {
    return maxPctValue;
  }

  return Math.floor(minPctValue + ((maxPctValue - minPctValue) * Math.log(value)) / Math.log(maxValue));
}

export function isTrackerVisible(summary: ISummary) {
  return getTrackerStatus(summary).step !== TrackerStep.HIDDEN;
}
